import FontAwesomePickerModal from '@components/FontAwesomePickerModal/FontAwesomePickerModal';
import { yupResolver } from '@hookform/resolvers/yup';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  Grid,
  Button,
  Box,
  Tooltip,
  Chip,
} from '@mui/material';
import { authContext } from '@staffedupusa/shared-components';
import { FC, useContext, useEffect, useState } from 'react';
import { Controller, useForm, useFieldArray, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import { EditIcon } from '../../../vectors/index';
import { useEditTemplate } from '../hooks/useEditTemplate';

type WhyWorkBlock = {
  f_icon?: string;
  heading?: string;
  description?: string;
};

type FormData = {
  why_we_work?: {
    heading?: string;
    blocks: WhyWorkBlock[];
  } | null;
};

const schema: yup.ObjectSchema<FormData> = yup.object({
  why_we_work: yup.object({
    heading: yup.string(),

    blocks: yup
      .array()
      .of(
        yup.object({
          heading: yup.string().required('Block heading is required'),
          description: yup.string().required('Block description is required'),
          f_icon: yup.string().required('Icon is required'),
        }),
      )
      .min(1, 'At least 1 block required')
      .max(4, 'Maximum 4 blocks allowed'),
  }),
});

type TemplateWhyWorkBlockModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  templateId?: number;
  whyWeWorkData?: {
    heading?: string;
    blocks: WhyWorkBlock[];
  } | null;
};

const TemplateWhyWorkBlockModal: FC<TemplateWhyWorkBlockModalProps> = ({
  isOpen,
  handleClose,
  whyWeWorkData,
  templateId,
}) => {
  const [errorText, setErrorText] = useState('');
  const {
    authData: { user },
  } = useContext(authContext);

  const whyWeWorkSource = whyWeWorkData ?? null;

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      why_we_work: {
        heading: whyWeWorkSource?.heading ?? '',
        blocks:
          (whyWeWorkSource?.blocks?.length ?? 0) > 0
            ? whyWeWorkSource?.blocks
            : [{ heading: '', description: '', f_icon: '' }],
      },
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'why_we_work.blocks',
  });

  // Reset state when modal closes
  const handleCloseAndReset = () => {
    reset({
      why_we_work: {
        heading: whyWeWorkSource?.heading ?? '',
        blocks:
          whyWeWorkSource?.blocks?.length > 0
            ? whyWeWorkSource.blocks
            : [{ heading: '', description: '', f_icon: '' }],
      },
    });
    handleClose();
  };

  const { editTemplate, isUpdatingTemplate } = useEditTemplate({
    setErrorText,
  });

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // remove "data:image/...;base64," prefix
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const onSubmit = async (values: FormData) => {
    const payload = {
      ...values,
      why_we_work: {
        ...values.why_we_work,
        heading: values.why_we_work?.heading ?? '',
        blocks: values.why_we_work?.blocks ?? [],
      },
      template_id: templateId,
    };

    const options = {
      onSuccess: (data: { message: string }) => {
        toast.success(data.message);
        handleCloseAndReset();
      },
    };

    editTemplate(payload, options);
  };

  useEffect(() => {
    if (whyWeWorkSource) {
      reset({
        why_we_work: {
          heading: whyWeWorkSource.heading ?? '',
          blocks:
            whyWeWorkSource.blocks?.length > 0
              ? whyWeWorkSource.blocks
              : [
                  {
                    heading: '',
                    description: '',
                    f_icon: '',
                  },
                ],
        },
      });
    }
  }, [user, reset, whyWeWorkSource]);

  // Decide modal size based on block count
  const blockCount = fields.length;
  let modalSize: 'xs' | 'sm' | 'md';
  if (blockCount === 1) {
    modalSize = 'xs';
  } else if (blockCount === 2) {
    modalSize = 'sm';
  } else {
    modalSize = 'md';
  }

  const getGridSize = () => {
    if (blockCount === 1) return 12;
    if (blockCount === 2) return 6;
    if (blockCount === 3) return 4;
    if (blockCount === 4) return 6;
    return 12;
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleCloseAndReset}
      maxWidth={modalSize}
      fullWidth
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleCloseAndReset}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <EditIcon />
        <br />
        <Typography variant="h4">Why Work Here Section</Typography>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)} style={{ marginBottom: 3 }}>
          <Stack spacing={3} sx={{ pt: 3 }}>
            {errorText && <Alert severity="error">{errorText}</Alert>}

            {/* Section heading */}
            <Controller
              control={control}
              name="why_we_work.heading"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Section Heading"
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={Boolean(errors.why_we_work?.heading)}
                  helperText={errors?.why_we_work?.heading?.message ?? ''}
                />
              )}
            />

            {/* Blocks */}
            <Grid container spacing={3} sx={{ ml: '-15px !important' }}>
              {fields.map((field, index) => (
                <Grid
                  item
                  xs={12}
                  md={getGridSize()}
                  key={field.id}
                  display="flex"
                >
                  <Stack
                    spacing={2}
                    sx={{
                      border: '1px solid #ddd',
                      p: 2,
                      borderRadius: 8,
                      backgroundColor: '#fafafa',
                      gap: 2,
                      width: '100%',
                    }}
                  >
                    {/* Block Title */}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Chip
                        label={`Block ${index + 1}`}
                        color="default"
                        size="small"
                        sx={{ fontWeight: '600' }}
                      />
                      {fields.length > 1 && (
                        <Tooltip title="Remove Block" placement="top" arrow>
                          <IconButton
                            onClick={() => {
                              if (fields.length > 1) remove(index);
                            }}
                            color="error"
                          >
                            <DeleteIcon sx={{ fontSize: '18px' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      {/* FontAwesome Picker */}
                      <Controller
                        control={control}
                        name={`why_we_work.blocks.${index}.f_icon`}
                        render={({ field }) => {
                          const hasError =
                            !!errors.why_we_work?.blocks?.[index]?.f_icon;

                          return (
                            <FontAwesomePickerModal
                              value={field.value}
                              onChange={field.onChange}
                              isError={hasError}
                            />
                          );
                        }}
                      />

                      {errors.why_we_work?.blocks?.[index]?.f_icon && (
                        <Typography variant="caption" color="error">
                          {errors.why_we_work.blocks[index].f_icon?.message}
                        </Typography>
                      )}
                    </Box>

                    {/* Block Heading */}
                    <Controller
                      control={control}
                      name={`why_we_work.blocks.${index}.heading`}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Block Heading"
                          variant="outlined"
                          size="small"
                          fullWidth
                          error={Boolean(
                            errors.why_we_work?.blocks?.[index]?.heading,
                          )}
                          helperText={
                            errors.why_we_work?.blocks?.[index]?.heading
                              ?.message ?? ''
                          }
                        />
                      )}
                    />

                    {/* Block Description */}
                    <Controller
                      control={control}
                      name={`why_we_work.blocks.${index}.description`}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Block Description"
                          variant="outlined"
                          size="small"
                          fullWidth
                          multiline
                          rows={4}
                          error={Boolean(
                            errors.why_we_work?.blocks?.[index]?.description,
                          )}
                          helperText={
                            errors.why_we_work?.blocks?.[index]?.description
                              ?.message ?? ''
                          }
                        />
                      )}
                    />
                  </Stack>
                </Grid>
              ))}
            </Grid>

            {/* Add Block button */}
            <Button
              variant={fields.length >= 4 ? 'contained' : 'outlined'}
              size="small"
              onClick={() =>
                append({ heading: '', description: '', icon: undefined })
              }
              disabled={fields.length >= 4}
            >
              {fields.length >= 4 ? 'Max Limit Reached...' : 'Add More Blocks'}
            </Button>

            {/* Save button */}
            <LoadingButton
              type="submit"
              variant="contained"
              size="small"
              fullWidth
              loading={isUpdatingTemplate}
              loadingPosition="start"
              color="secondary"
              startIcon={<CheckIcon />}
              style={{
                marginTop: '25px',
                fontSize: '16px',
                padding: '7px 15px',
              }}
            >
              Save
            </LoadingButton>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateWhyWorkBlockModal;
